import { create } from 'zustand';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import { supabase } from '@/lib/supabase';
import { v4 as uuidv4 } from 'uuid';
// Define types locally since they're not yet exported from core
export interface SavingGoal {
  id: string;
  user_id: string;
  name: string;
  target_amount: number;
  current_amount: number;
  currency: string;
  target_date: string | null;
  deadline?: string | null;
  account_id: string; // Required in updated model
  icon: string | null;
  color: string | null;
  description: string | null;
  status: 'active' | 'paused' | 'completed'; // Add status field
  is_completed: boolean;
  created_at: string;
  updated_at: string;
}

interface SavingGoalsState {
  goals: SavingGoal[];
  isLoading: boolean;
  error: string | null;
}

export interface WebSavingGoalsStore extends SavingGoalsState {
  fetchGoals: () => Promise<void>;
  addGoal: (goal: Partial<Omit<SavingGoal, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'is_completed'>> & { 
    notes?: string | null;    // Will be mapped to description
    target_date?: string | null; // Will be mapped to deadline
    account_id: string;       // Required field
  }) => Promise<SavingGoal | undefined>;
  updateGoal: (goalId: string, updates: Partial<SavingGoal>) => Promise<void>;
  deleteGoal: (goalId: string) => Promise<void>;
  contributeToGoal: (goalId: string, amount: number) => Promise<void>;
  withdrawFromGoal: (goalId: string, amount: number) => Promise<void>;
  markGoalAsCompleted: (goalId: string) => Promise<void>;
}

export const useSavingGoalsStore = create<WebSavingGoalsStore>((set, get) => ({
  goals: [],
  isLoading: false,
  error: null,

  fetchGoals: async () => {
    try {
      set({ isLoading: true, error: null });
      // Get user from auth store's state directly
      const user = useAuthStore.getState().user;
      
      if (!user?.id) {
        console.error('User not authenticated');
        set({ isLoading: false });
        return;
      }
      
      const { data, error } = await supabase
        .from('saving_goals')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Map database fields to our application model
      const mappedData = data?.map(goal => {
        // Create a new object to avoid mutating the original
        const mappedGoal: Record<string, any> = { ...goal };
        
        // Map deadline to target_date if it exists
        if ('deadline' in mappedGoal) {
          mappedGoal.target_date = mappedGoal.deadline;
          // We can keep deadline in the object, as it's not used in the app
        }
        
        return mappedGoal;
      }) || [];
      
      set({ goals: mappedData as SavingGoal[], isLoading: false });
    } catch (error) {
      console.error('Error fetching saving goals:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to fetch saving goals', isLoading: false });
      toast.error('Failed to fetch saving goals');
    }
  },

  addGoal: async (goalData) => {
    try {
      set({ isLoading: true, error: null });
      const { user } = useAuthStore.getState();
      
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      
      // Validate that account_id is provided
      if (!goalData.account_id) {
        throw new Error('Savings account is required');
      }
      
      // Extract fields from goalData and map to correct database fields
      const { notes, target_date, ...restData } = goalData;
      
      // Create the full data object with valid database fields, mapping to the correct column names
      const fullData = {
        id: uuidv4(), // Generate a UUID for the id field
        ...restData,
        // Map field names to match database schema
        description: notes || null,
        deadline: target_date, // Map target_date to deadline in the database
        user_id: user.id,
        is_completed: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: goalData.status || 'active'
      };
      
      // Log what we're sending to help with debugging
      console.log("Saving goal data:", fullData);
      
      const { data, error } = await supabase
        .from("saving_goals")
        .insert([fullData])
        .select("*")
        .single();
      
      if (error) {
        console.error("Error adding saving goal:", error);
        throw error;
      }
      
      // Update the store
      const updatedGoals = [...get().goals, data];
      set({ goals: updatedGoals, isLoading: false });
      
      toast.success('Saving goal created successfully');
      return data;
    } catch (error) {
      console.error("Error adding saving goal:", error);
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error occurred', 
        isLoading: false 
      });
      toast.error('Failed to create saving goal');
      throw error;
    }
  },

  updateGoal: async (goalId, updates) => {
    try {
      set({ isLoading: true, error: null });
      
      // Map fields to match database schema if needed
      const mappedUpdates: Record<string, any> = { ...updates };
      
      // Map target_date to deadline
      if ('target_date' in mappedUpdates) {
        mappedUpdates.deadline = mappedUpdates.target_date;
        delete mappedUpdates.target_date;
      }
      
      // Map notes to description if present
      if ('notes' in mappedUpdates) {
        mappedUpdates.description = mappedUpdates.notes as string | null;
        delete mappedUpdates.notes;
      }
      
      const { error } = await supabase
        .from('saving_goals')
        .update({
          ...mappedUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', goalId);
      
      if (error) throw error;
      
      set(state => ({
        goals: state.goals.map(goal => 
          goal.id === goalId ? { ...goal, ...updates, updated_at: new Date().toISOString() } : goal
        ),
        isLoading: false
      }));
      
      toast.success('Saving goal updated successfully');
    } catch (error) {
      console.error('Error updating saving goal:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update saving goal', isLoading: false });
      toast.error('Failed to update saving goal');
      throw error;
    }
  },

  deleteGoal: async (goalId) => {
    try {
      set({ isLoading: true, error: null });
      
      const { error } = await supabase
        .from('saving_goals')
        .delete()
        .eq('id', goalId);
      
      if (error) throw error;
      
      set(state => ({
        goals: state.goals.filter(goal => goal.id !== goalId),
        isLoading: false
      }));
      
      toast.success('Saving goal deleted successfully');
    } catch (error) {
      console.error('Error deleting saving goal:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to delete saving goal', isLoading: false });
      toast.error('Failed to delete saving goal');
      throw error;
    }
  },

  contributeToGoal: async (goalId, amount) => {
    try {
      set({ isLoading: true, error: null });
      
      // Get the current goal first
      const currentGoal = get().goals.find(g => g.id === goalId);
      if (!currentGoal) {
        throw new Error('Goal not found');
      }
      
      // Update goal with new amount
      const { error } = await supabase
        .from('saving_goals')
        .update({
          current_amount: currentGoal.current_amount + amount,
          updated_at: new Date().toISOString()
        })
        .eq('id', goalId);
      
      if (error) throw error;
      
      // Create a transaction for this contribution
      // This would be implemented in a transactions store
      
      // Update goals state
      set(state => ({
        goals: state.goals.map(goal => 
          goal.id === goalId 
            ? { 
                ...goal, 
                current_amount: goal.current_amount + amount,
                updated_at: new Date().toISOString() 
              } 
            : goal
        ),
        isLoading: false
      }));
      
      toast.success('Contribution added successfully');
    } catch (error) {
      console.error('Error contributing to saving goal:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to add contribution', isLoading: false });
      toast.error('Failed to add contribution');
      throw error;
    }
  },

  withdrawFromGoal: async (goalId, amount) => {
    try {
      set({ isLoading: true, error: null });
      
      // Get the current goal first
      const currentGoal = get().goals.find(g => g.id === goalId);
      if (!currentGoal) {
        throw new Error('Goal not found');
      }
      
      if (currentGoal.current_amount < amount) {
        throw new Error('Insufficient funds in this saving goal');
      }
      
      // Update goal with new amount
      const { error } = await supabase
        .from('saving_goals')
        .update({
          current_amount: currentGoal.current_amount - amount,
          updated_at: new Date().toISOString()
        })
        .eq('id', goalId);
      
      if (error) throw error;
      
      // Create a transaction for this withdrawal
      // This would be implemented in a transactions store
      
      // Update goals state
      set(state => ({
        goals: state.goals.map(goal => 
          goal.id === goalId 
            ? { 
                ...goal, 
                current_amount: goal.current_amount - amount,
                updated_at: new Date().toISOString() 
              } 
            : goal
        ),
        isLoading: false
      }));
      
      toast.success('Withdrawal completed successfully');
    } catch (error) {
      console.error('Error withdrawing from saving goal:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to process withdrawal', isLoading: false });
      toast.error('Failed to process withdrawal');
      throw error;
    }
  },

  markGoalAsCompleted: async (goalId) => {
    try {
      set({ isLoading: true, error: null });
      
      const { error } = await supabase
        .from('saving_goals')
        .update({
          status: 'completed',
          is_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', goalId);
      
      if (error) throw error;
      
      set(state => ({
        goals: state.goals.map(goal => 
          goal.id === goalId 
            ? { 
                ...goal, 
                status: 'completed',
                is_completed: true,
                updated_at: new Date().toISOString() 
              } 
            : goal
        ),
        isLoading: false
      }));
      
      toast.success('Goal marked as completed');
    } catch (error) {
      console.error('Error marking goal as completed:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to mark goal as completed', isLoading: false });
      toast.error('Failed to mark goal as completed');
      throw error;
    }
  }
})); 